/**
 * "Make it darker." "Add a phone field." — changing a published app without rebuilding it.
 *
 * The model is given the app it already wrote and asked for the change, so the parts the
 * user did not mention stay as they are. That matters more here than anywhere else in
 * the pipeline: a refinement that silently reshuffles an app the owner already shared is
 * worse than one that fails, because the link is already out.
 *
 * Two things are deliberately preserved, and tested:
 *   - The app id, which IS the localStorage namespace. Change it and every existing
 *     visitor's data vanishes.
 *   - The published URL, since `hostOnSaddle` republishes to the same name-based slug.
 */
import { gateWebApp } from './gate';
import { buildWebAppPrompt } from './prompt';
import {
  buildRepairPrompt,
  normalizeAppCode,
  MAX_REPAIR_ROUNDS,
  WebAppGenerationError,
  type WebAppGenerationIO,
} from './generateWebApp';

export interface RefineResult {
  appCode: string;
  attempts: number;
  gateFailures: number;
}

export async function refineWebApp(
  currentSource: string,
  instruction: string,
  io: WebAppGenerationIO,
  signedIn: boolean,
  goal?: string,
): Promise<RefineResult> {
  if (!currentSource.trim()) throw new Error('There is no app to change yet.');

  const systemPrompt = buildWebAppPrompt();
  // Presented as the model's OWN previous turn, which is what makes "change this one
  // thing" behave like an edit rather than a fresh build that happens to share a subject.
  const messages: { role: 'user' | 'assistant'; content: string }[] = [
    { role: 'user', content: goal ? `Build this app: ${goal}` : 'Build this app.' },
    { role: 'assistant', content: currentSource },
    {
      role: 'user',
      content: [
        `Change the app: ${instruction}`,
        '',
        'Return the COMPLETE app body again with that change applied. Keep everything else',
        'exactly as it is — the same screens, the same copy, the same kit, the same seeded',
        'data and the same S.store collection names. Someone is already using this app, so a',
        'renamed collection would throw away everything they have saved.',
      ].join('\n'),
    },
  ];

  let lastCode = '';
  let lastDiagnostics: string[] = [];
  let gateFailures = 0;

  for (let attempt = 1; attempt <= MAX_REPAIR_ROUNDS + 1; attempt++) {
    const { content } = await io.generate({ systemPrompt, messages, signedIn, structured: true });
    const appCode = normalizeAppCode(content);
    lastCode = appCode;

    const result = gateWebApp(appCode);
    if (result.ok) return { appCode, attempts: attempt, gateFailures };

    gateFailures++;
    lastDiagnostics = result.diagnostics;
    messages.push({ role: 'assistant', content: appCode });
    messages.push({ role: 'user', content: buildRepairPrompt(appCode, result.diagnostics) });
  }

  throw new WebAppGenerationError(
    `The change still had problems after ${MAX_REPAIR_ROUNDS} fix attempts.`,
    lastDiagnostics,
    lastCode,
    gateFailures,
  );
}

/** The S.store collections an app keeps, read from its source. A refinement that drops
 *  one is a refinement that deletes a user's data, so the caller can refuse it. */
export function storeCollections(source: string): string[] {
  const found = new Set<string>();
  for (const m of source.matchAll(/S\.store\(\s*['"]([A-Za-z][A-Za-z0-9_]*)['"]/g)) found.add(m[1]);
  return [...found];
}

/** Collections present before and gone after — the data this change would strand. */
export function droppedCollections(before: string, after: string): string[] {
  const now = new Set(storeCollections(after));
  return storeCollections(before).filter((c) => !now.has(c));
}
